// V3 shell — "Bento" card-first layout.
//
//   ┌──┬──────────────────────────────────────────┐
//   │ ◆│ Dashboard · breadcrumb   🔍 V3 ☀ ⚙       │
//   │  ├──────────────────────────────────────────┤
//   │  │                                          │
//   │ □│  ┌──────────┐ ┌──────────────────────┐  │
//   │ □│  │ tile     │ │ tile (wide)          │  │
//   │ □│  └──────────┘ └──────────────────────┘  │
//   │ □│  ┌──────────────────────────────────┐    │
//   │ □│  │ tile (full width)                │    │
//   │  │  └──────────────────────────────────┘    │
//   │  │                                          │
//   │ ⏻│                                          │
//   └──┴──────────────────────────────────────────┘
//
// Left "rail" is an icon-only column (64px wide on desktop, hidden on mobile
// behind a drawer). The page area is full width with a slim breadcrumb +
// controls strip at the top. Pages aren't restructured here — Panels render
// inside and get bento-tile styling via the [data-version="v3"] CSS layer.

import React, { useEffect, useState, useCallback } from "react";
import { LogOut, Menu, Search, Sun, Moon, X, MoreHorizontal, Keyboard, Monitor, Smartphone, Landmark } from "lucide-react";
import { loadState, logout } from "../api";
import { nav, can } from "../nav";
import { canEnterWorkspace } from "../data";
import Module from "./Module";
import WelcomeOverlay from "./WelcomeOverlay";
import { useTheme } from "./Theme";
import { useVersion } from "./Version";
import { useWorkspace } from "./Workspace";
import { ActiveTeamProvider } from "./ActiveTeam";
import TeamSwitcher from "./TeamSwitcher";
import { useUnreadMessages } from "./useUnreadMessages";
import { useAutoReload } from "./useAutoReload";
import NotificationBell from "./NotificationBell";
import ChatFab from "./ChatFab";

const EMPTY_STATE = {
  members: [], groups: [], teams: [], matches: [], availability: [],
  squads: [], announcements: [], transactions: [], notifications: []
};

export default function ShellV3({ user, setUser }) {
  const [state, setState]   = useState(EMPTY_STATE);
  const [active, setActive] = useState("dashboard");
  // Optional payload carried alongside a navigation (see Shell.jsx).
  const [navArg, setNavArg] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const unreadMessages = useUnreadMessages({ active, onNavigate: setActive });
  const [error, setError]     = useState("");
  function navigate(key, arg = null) {
    setActive(key);
    setNavArg(arg);
  }
  const { theme, toggleTheme } = useTheme();
  const { version, setVersion } = useVersion();
  const { setWorkspace }        = useWorkspace();
  const canGoLeague = canEnterWorkspace(user, "league");

  const [showWelcome, setShowWelcome] = useState(
    () => sessionStorage.getItem("gabruz-just-logged-in") === "1"
  );
  useEffect(() => {
    if (showWelcome) sessionStorage.removeItem("gabruz-just-logged-in");
  }, [showWelcome]);

  // Overflow menu — collapses theme toggle, keyboard shortcut hint and
  // (for admins) version switch into a single "…" so the topbar stops
  // reading as a control panel. Closes on outside-click / Escape.
  const [overflowOpen, setOverflowOpen] = useState(false);
  useEffect(() => {
    if (!overflowOpen) return;
    const onClick = (e) => {
      if (!e.target.closest?.(".topbarOverflow")) setOverflowOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOverflowOpen(false);
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [overflowOpen]);
  const canPreviewVersion = (user.roles || []).some(r =>
    ["ADMIN","PRESIDENT","VICE_PRESIDENT"].includes(r)
  );

  const reload = useCallback(async () => {
    setError("");
    try {
      const next = await loadState();
      setState(next);
    } catch (err) {
      if (err.status === 401) { setUser(null); return; }
      setError(err.message || "Failed to load data");
    }
  }, [setUser]);

  // Cross-device sync — see useAutoReload for details.
  useAutoReload(reload);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      await reload();
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, [reload]);

  // V3 uses the modern mobile-first component set for the main views
  // (Schedule / Squads / etc.), so it also honours the `desktopOnly`
  // nav flag — Availability is a legacy admin-planning matrix that
  // shouldn't clutter the V3 nav; admins triage RSVPs inline on
  // Schedule cards instead.
  const allowed = nav.filter(n => !n.desktopOnly && can(user, n.key));
  const activeItem = allowed.find(n => n.key === active);

  function signOut() {
    logout();
    setUser(null);
  }
  function navigateTo(key) {
    setActive(key);
    setDrawerOpen(false);
  }

  const initial = (user.name || "?")[0]?.toUpperCase() || "?";

  return (
    <ActiveTeamProvider user={user} teams={state.teams}>
    <div className={`appV3${showWelcome ? " app--welcoming" : ""}`}>
      {showWelcome && <WelcomeOverlay onDone={() => setShowWelcome(false)} />}

      {/* ----- Left icon rail (desktop) ----- */}
      <aside className="appV3-rail">
        <a className="appV3-rail-brand" href="#" onClick={(e) => { e.preventDefault(); navigateTo("dashboard"); }} title="Gabruz Universe">
          <img src="/gabruz-brand.png" onError={e => e.currentTarget.style.display = "none"} alt="Gabruz Universe" />
        </a>
        <nav className="appV3-rail-nav" aria-label="Main">
          {allowed.map(item => {
            const Icon = item.icon;
            const itemBadge = item.key === "messages" && unreadMessages > 0
              ? unreadMessages : null;
            return (
              <button
                key={item.key}
                type="button"
                className={active === item.key ? "appV3-rail-btn appV3-rail-btn--active" : "appV3-rail-btn"}
                onClick={() => navigateTo(item.key)}
                title={item.label}
                aria-label={item.label}
              >
                <span className="appV3-rail-iconWrap">
                  <Icon size={18} />
                  {itemBadge && (
                    <span className="appV3-rail-badge">{itemBadge > 99 ? "99+" : itemBadge}</span>
                  )}
                </span>
                <span className="appV3-rail-label">{item.label}</span>
              </button>
            );
          })}
        </nav>
        <button className="appV3-rail-btn appV3-rail-out" onClick={signOut} title="Sign out" aria-label="Sign out">
          <LogOut size={18} />
          <span className="appV3-rail-label">Sign Out</span>
        </button>
      </aside>

      {/* ----- Right column: top strip + bento page ----- */}
      <div className="appV3-content">
        <header className="appV3-top">
          <button className="appV3-burger" onClick={() => setDrawerOpen(true)} aria-label="Open menu">
            <Menu size={18} />
          </button>
          <NotificationBell notifications={state.notifications} onNavigate={navigateTo}/>
          <div className="appV3-crumb">
            <span className="appV3-crumb-trail">Gabruz Universe ·</span>
            <span className="appV3-crumb-active">{activeItem?.label || "Dashboard"}</span>
          </div>

          <TeamSwitcher />

          <div className="appV3-search">
            <Search size={15} />
            <input placeholder="Search…" />
          </div>

          <div className="topbarOverflow">
            <button
              type="button"
              className="themeToggle"
              onClick={() => setOverflowOpen(o => !o)}
              title="More options"
              aria-label="More options"
              aria-haspopup="menu"
              aria-expanded={overflowOpen}
            >
              <MoreHorizontal size={16}/>
            </button>
            {overflowOpen && (
              <div className="topbarOverflow-menu" role="menu">
                {canGoLeague && (
                  <>
                    <button
                      type="button"
                      role="menuitem"
                      className="topbarOverflow-item"
                      onClick={() => { setWorkspace("league"); setOverflowOpen(false); }}
                    >
                      <Landmark size={14}/>
                      <span>Switch to League workspace</span>
                    </button>
                    <div className="topbarOverflow-sep" role="separator"/>
                  </>
                )}
                <button
                  type="button"
                  role="menuitem"
                  className="topbarOverflow-item"
                  onClick={() => { toggleTheme(); setOverflowOpen(false); }}
                >
                  {theme === "light" ? <Moon size={14}/> : <Sun size={14}/>}
                  <span>Switch to {theme === "light" ? "dark" : "light"} theme</span>
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="topbarOverflow-item"
                  onClick={() => {
                    setOverflowOpen(false);
                    window.dispatchEvent(new KeyboardEvent("keydown", { key: "?" }));
                  }}
                >
                  <Keyboard size={14}/>
                  <span>Keyboard shortcuts</span>
                </button>
                {canPreviewVersion && (
                  <>
                    <div className="topbarOverflow-sep" role="separator"/>
                    <div className="topbarOverflow-label">Preview shell</div>
                    <button
                      type="button"
                      role="menuitemradio"
                      aria-checked={version === "v3"}
                      className={`topbarOverflow-item${version === "v3" ? " topbarOverflow-item--active" : ""}`}
                      onClick={() => { setVersion("v3"); setOverflowOpen(false); }}
                    >
                      <Monitor size={14}/>
                      <span>Desktop</span>
                    </button>
                    <button
                      type="button"
                      role="menuitemradio"
                      aria-checked={version === "v4"}
                      className={`topbarOverflow-item${version === "v4" ? " topbarOverflow-item--active" : ""}`}
                      onClick={() => { setVersion("v4"); setOverflowOpen(false); }}
                    >
                      <Smartphone size={14}/>
                      <span>Mobile</span>
                    </button>
                  </>
                )}
              </div>
            )}
          </div>

          <div className="appV3-me" title={user.email}>
            <div className="appV3-me-avatar">{user.avatar || initial}</div>
            <div className="appV3-me-text">
              <strong>{user.name}</strong>
              <span>{user.roles[0].replace("_", " ")}</span>
            </div>
          </div>
        </header>

        <main className="appV3-main">
          <div className="page page--v3">
            {loading ? (
              <div className="empty">
                <h2>Loading…</h2>
                <p>Fetching data from the API.</p>
              </div>
            ) : error ? (
              <div className="empty">
                <h2>Couldn't load data</h2>
                <p>{error}</p>
                <button className="primary" onClick={reload}>Retry</button>
              </div>
            ) : (
              <Module active={active} setActive={navigate} navArg={navArg} state={state} reload={reload} user={user} setUser={setUser} />
            )}
          </div>
        </main>
      </div>

      {/* ----- Mobile slide-in drawer ----- */}
      {drawerOpen && (
        <>
          <div className="appV3-drawer-veil" onClick={() => setDrawerOpen(false)} />
          <aside className="appV3-drawer">
            <div className="appV3-drawer-head">
              <strong>Navigation</strong>
              <button onClick={() => setDrawerOpen(false)} aria-label="Close menu"><X size={18}/></button>
            </div>
            {allowed.map(item => {
              const Icon = item.icon;
              return (
                <button
                  key={item.key}
                  type="button"
                  className={active === item.key ? "appV3-drawer-link appV3-drawer-link--active" : "appV3-drawer-link"}
                  onClick={() => navigateTo(item.key)}
                >
                  <Icon size={16} /><span>{item.label}</span>
                </button>
              );
            })}
            <button className="appV3-drawer-link appV3-drawer-out" onClick={signOut}>
              <LogOut size={16} /> Sign Out
            </button>
          </aside>
        </>
      )}
      <ChatFab active={active} unread={unreadMessages} onOpen={() => setActive("messages")} />
    </div>
    </ActiveTeamProvider>
  );
}
